import {
    applyCreateCommentThread,
    applyDeleteCommentThread,
    applyIncrementCommentThreadCommentCount,
    applyPinCommentThread,
    applyResolveCommentThread,
    applyUnresolveCommentThread,
    applyUpdateCommentThreadsPositionAnchor,
} from "../actions/commentThreadActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type CommentSliceState = Pick<
    DocumentStoreState,
    | "createCommentThread"
    | "deleteCommentThread"
    | "pinCommentThread"
    | "resolveCommentThread"
    | "unresolveCommentThread"
    | "incrementCommentThreadCommentCount"
    | "updateCommentThreadsPositionAnchor"
>;

export function createCommentSlice(deps: SliceDependencies): CommentSliceState {
    const {primitives, get} = deps;

    return {
        createCommentThread: (commentThread) => {
            const document = get().document;
            if (!document?.comment_threads) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyCreateCommentThread(draft, commentThread), {
                actionType: "createCommentThread",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        deleteCommentThread: (commentThreadUid) => {
            const document = get().document;
            if (!document?.comment_threads || !commentThreadUid) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyDeleteCommentThread(draft, commentThreadUid), {
                actionType: "deleteCommentThread",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        pinCommentThread: (commentThreadUid, pinned) => {
            const document = get().document;
            if (!document?.comment_threads?.[commentThreadUid]) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyPinCommentThread(draft, commentThreadUid, pinned), {
                actionType: "pinCommentThread",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        resolveCommentThread: (commentThreadUid) => {
            const document = get().document;
            if (!document?.comment_threads?.[commentThreadUid]) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyResolveCommentThread(draft, commentThreadUid), {
                actionType: "resolveCommentThread",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        unresolveCommentThread: (commentThreadUid) => {
            const document = get().document;
            if (!document?.comment_threads?.[commentThreadUid]) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyUnresolveCommentThread(draft, commentThreadUid), {
                actionType: "unresolveCommentThread",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        incrementCommentThreadCommentCount: (commentThreadUid) => {
            const document = get().document;
            if (!document?.comment_threads?.[commentThreadUid]) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applyIncrementCommentThreadCommentCount(draft, commentThreadUid),
                {
                    actionType: "incrementCommentThreadCommentCount",
                    canUndo: false,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: false,
                },
            );
        },

        updateCommentThreadsPositionAnchor: (commentThreads) => {
            const document = get().document;
            if (!document?.comment_threads || commentThreads.length === 0) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applyUpdateCommentThreadsPositionAnchor(draft, commentThreads),
                {
                    actionType: "updateCommentThreadsPositionAnchor",
                    canUndo: false,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: false,
                },
            );
        },
    };
}
